import { useEffect, useLayoutEffect, useRef, useState } from "react";
import gsap from "gsap";
import logoMRight from "../assets/images/icons/M-right-v2.svg";
import logoMLeft from "../assets/images/icons/M-left.svg";
import logoA from "../assets/images/icons/A-logo.svg";
import logoF from "../assets/images/icons/F-logo.svg";

type FamLogoLoaderProps = {
  onAnimationComplete?: () => void;
};

const FamLogoLoader = ({ onAnimationComplete }: FamLogoLoaderProps) => {
  const onAnimationCompleteRef = useRef(onAnimationComplete);
  onAnimationCompleteRef.current = onAnimationComplete;
  const [isMobileSmall, setIsMobileSmall] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 480 : false
  );
  const logoMLeftRef = useRef<HTMLImageElement>(null);
  const logoMRightRef = useRef<HTMLImageElement>(null);
  const logoFRef = useRef<HTMLImageElement>(null);
  const logoARef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const handleResize = () => setIsMobileSmall(window.innerWidth <= 480);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (
        !logoMLeftRef.current ||
        !logoMRightRef.current ||
        !logoFRef.current ||
        !logoARef.current
      ) {
        return;
      }

      const tl = gsap.timeline({
        onComplete: () => onAnimationCompleteRef.current?.(),
      });

      tl.fromTo(
        logoMLeftRef.current,
        { x: "-240%", opacity: 0 },
        { x: "-120%", opacity: 1, duration: 1, ease: "power2.out" }
      )
        .fromTo(
          logoMRightRef.current,
          { x: "0%", opacity: 0 },
          { x: "-104%", opacity: 1, duration: 1, ease: "power2.out" },
          "<"
        )
        .to([logoMLeftRef.current, logoMRightRef.current], {
          x: "-40%",
          duration: 0.5,
          ease: "power2.out",
        })
        .fromTo(
          logoFRef.current,
          { x: "100%", opacity: 0 },
          { x: "-3%", opacity: 1, duration: 0.5, ease: "power2.out" },
          "<"
        )
        .fromTo(
          logoARef.current,
          { x: "100%", opacity: 0 },
          { x: "-9%", opacity: 1, duration: 0.5, ease: "power2.out" },
          "<"
        );
    });

    return () => ctx.revert();
  }, []);

  return (
    <div
      className="fam-logo-loader"
      style={{
        position: "relative",
        width: "190px",
        height: "50px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        maxWidth: "100%",
      }}
    >
      <img
        src={logoF}
        alt="F"
        ref={logoFRef}
        style={{
          position: "absolute",
          top: 0,
          left: isMobileSmall ? "16.6%" : "16%",
        }}
      />
      <img
        src={logoA}
        alt="A"
        ref={logoARef}
        style={{
          position: "absolute",
          top: 0,
          left: isMobileSmall ? "32.5%" : "32%",
        }}
      />
      <img
        src={logoMLeft}
        alt="M Left"
        ref={logoMLeftRef}
        style={{
          position: "absolute",
          top: "0px",
          left: isMobileSmall ? "64%" : "64%",
        }}
      />
      <img
        src={logoMRight}
        alt="M Right"
        ref={logoMRightRef}
        style={{
          position: "absolute",
          top: "-4px",
          left: isMobileSmall ? "75%" : "76%",
        }}
      />
    </div>
  );
};

export default FamLogoLoader;
